import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlayCircle, PauseCircle, StopCircle, Timer, Maximize2, Minimize2, Bell, CheckCircle, AlertCircle } from 'lucide-react';
import { useDisciplineStore } from '../../contexts/disciplineStore';
import { useTimerUpdates } from '../../hooks/useTimerUpdates';
import './timer-performance-fixes.css';

/**
 * ImprovedTimerSlider - A modern, user-friendly timer management component
 * Features:
 * - Horizontal slider for multiple timers (up to 5)
 * - Expandable cards with detailed view
 * - Real-time progress tracking with automatic completion
 * - Intuitive controls with visual feedback
 * - Responsive design with touch support
 * - Notification system integration
 * - Enhanced error handling and user feedback
 * - Performance optimizations to prevent flickering
 */
const ImprovedTimerSlider = React.memo(({ isDarkMode = false, className = '', onNotification }) => {
  const {
    pauseTimer,
    resumeTimer,
    stopTimer,
    completeTimer,
    goals = []
  } = useDisciplineStore();

  // Use custom hook for real-time updates
  const { calculateRemainingTime, activeTimers } = useTimerUpdates();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedTimer, setExpandedTimer] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [completingTimers, setCompletingTimers] = useState(new Set());
  const [timerStates, setTimerStates] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(Date.now());
  const containerRef = useRef(null);
  const updateTimeoutRef = useRef(null);  // Optimized progress calculation with caching to prevent excessive recalculations
  const calculateProgress = useCallback((timer) => {
    if (!timer || timer.isCompleted) return 100;
    
    const totalDuration = (timer.duration || 25) * 60; // in seconds
    const remaining = calculateRemainingTime(timer);
    const progress = Math.max(0, Math.min(100, ((totalDuration - remaining) / totalDuration) * 100));
    
    return Math.round(progress * 10) / 10; // Round to 1 decimal place for stability
  }, [calculateRemainingTime]);

  // Debounced update function to prevent excessive re-renders
  const debouncedUpdate = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }
    
    updateTimeoutRef.current = setTimeout(() => {
      setLastUpdateTime(Date.now());
    }, 50); // 50ms debounce
  }, []);

  // Auto-advance to next timer if current is completed
  useEffect(() => {
    if (activeTimers.length > 0 && currentIndex >= activeTimers.length) {
      setCurrentIndex(0);
    }
    debouncedUpdate();
  }, [activeTimers.length, currentIndex, debouncedUpdate]);// Create a stable reference for active timer IDs
  const activeTimerIds = useMemo(() => 
    activeTimers.map(t => t.id).join(','), 
    [activeTimers]
  );  // Stable timer updates without flickering - optimized for consistent display
  useEffect(() => {
    if (activeTimers.length === 0) {
      setTimerStates({});
      return;
    }

    let intervalId;

    const updateTimerStates = () => {
      const now = Date.now();
      
      setTimerStates(prevStates => {
        const newStates = { ...prevStates };
        let hasChanges = false;

        activeTimers.forEach(timer => {
          const remainingTime = calculateRemainingTime(timer);
          const progress = calculateProgress(timer);
          const prevState = prevStates[timer.id];
          
          // Only update if there's a significant change (reduces flickering)
          const shouldUpdate = !prevState || 
            Math.abs((prevState.remainingTime || 0) - remainingTime) >= 1 ||
            Math.abs((prevState.progress || 0) - progress) >= 0.5;
          
          if (shouldUpdate) {
            newStates[timer.id] = {
              remainingTime: Math.max(0, Math.round(remainingTime)),
              progress: progress,
              isNearCompletion: remainingTime <= 60 && remainingTime > 0,
              isCompleting: remainingTime <= 5 && remainingTime > 0,
              lastUpdate: now
            };
            hasChanges = true;
          }
        });

        // Clean up states for removed timers
        Object.keys(newStates).forEach(timerId => {
          if (!activeTimers.find(t => t.id === timerId)) {
            delete newStates[timerId];
            hasChanges = true;
          }
        });
        
        return hasChanges ? newStates : prevStates;
      });
    };

    // Update every 1000ms for stable, non-flickering display
    intervalId = setInterval(updateTimerStates, 1000);
    updateTimerStates(); // Initial update

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [activeTimers, activeTimerIds, calculateRemainingTime, calculateProgress]);
  // Format time display (memoized)
  const formatTime = useCallback((seconds) => {
    const mins = Math.floor(Math.max(0, seconds) / 60);
    const secs = Math.max(0, seconds) % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);
  // Handle timer actions with enhanced feedback
  const handleTimerAction = async (action, timerId) => {
    try {
      const timer = activeTimers.find(t => t.id === timerId);
      if (!timer) return;

      setCompletingTimers(prev => new Set(prev).add(timerId));      switch (action) {
        case 'pause':
          await pauseTimer(timerId);
          if (onNotification) {
            onNotification(`${timer.goalName} paused`, 'info');
          }
          break;
        case 'resume':
          await resumeTimer(timerId);
          if (onNotification) {
            onNotification(`${timer.goalName} resumed`, 'info');
          }
          break;
        case 'stop':
          const progress = calculateProgress(timer);
          
          if (progress < 90) {
            const confirmed = window.confirm(
              `Timer is only ${Math.round(progress)}% complete. Stopping early will forfeit XP rewards.\n\nAre you sure you want to stop?`
            );
            if (!confirmed) {
              setCompletingTimers(prev => {
                const newSet = new Set(prev);
                newSet.delete(timerId);
                return newSet;
              });
              return;
            }
          }
          
          await stopTimer(timerId);
          if (onNotification) {
            onNotification(`${timer.goalName} stopped`, 'warning');
          }
          break;
        case 'complete':
          await completeTimer(timerId);
          if (onNotification) {
            onNotification(`${timer.goalName} completed! +${timer.xpReward} XP`, 'success');
          }
          break;
        default:
          console.warn(`Unknown timer action: ${action}`);
          break;
      }
    } catch (error) {
      console.error('Timer action failed:', error);
      if (onNotification) {
        onNotification('Action failed. Please try again.', 'error');
      }
    } finally {
      setTimeout(() => {
        setCompletingTimers(prev => {
          const newSet = new Set(prev);
          newSet.delete(timerId);
          return newSet;
        });
      }, 1000);
    }
  };

  // Navigation functions
  const goToPrevTimer = () => {
    setCurrentIndex(prev => prev === 0 ? activeTimers.length - 1 : prev - 1);
  };

  const goToNextTimer = () => {
    setCurrentIndex(prev => (prev + 1) % activeTimers.length);
  };
  // Touch handlers for swipe gestures and tap-to-center
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) { // Minimum swipe distance
      if (diff > 0) {
        goToNextTimer();
      } else {
        goToPrevTimer();
      }
    }
    
    setTouchStart(null);
  };
  // Handle tap to center functionality with improved user feedback
  const handleTimerTap = (timerIndex, event) => {
    // Prevent event bubbling
    if (event) {
      event.stopPropagation();
    }
    
    if (timerIndex !== currentIndex) {
      setCurrentIndex(timerIndex);
      
      // Provide haptic feedback on mobile devices
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Optional: Add visual feedback through temporary highlighting
      const cardElement = event?.currentTarget;
      if (cardElement) {
        cardElement.style.transform = 'scale(1.05)';
        setTimeout(() => {
          cardElement.style.transform = '';
        }, 150);
      }
    }
  };
  // Timer Card Component
  const TimerCard = ({ timer, index, isActive, isExpanded }) => {
    const timerState = timerStates[timer.id] || {};
    const { remainingTime = 0, progress = 0, isNearCompletion = false, isCompleting = false } = timerState;
    const isPaused = timer.status === 'paused' || timer.isPaused;
    const goal = goals.find(g => g.id === timer.goalId);
    const isProcessing = completingTimers.has(timer.id);    const cardVariants = {
      hidden: { opacity: 0, scale: 0.98 },
      visible: { opacity: 1, scale: 1 },
      expanded: { opacity: 1, scale: 1.01 }
    };

    // Stabilized border color (memoized to prevent constant changes)
    const borderColor = useMemo(() => {
      if (isProcessing) return '#10b981';
      if (isCompleting) return '#ef4444';
      if (isNearCompletion) return '#f59e0b';
      if (isPaused) return '#f59e0b';
      return '#10b981';
    }, [isProcessing, isCompleting, isNearCompletion, isPaused]);    // Stabilized background styles to prevent constant re-renders
    const cardStyles = useMemo(() => ({
      background: isDarkMode 
        ? 'linear-gradient(135deg, rgba(31, 41, 55, 0.9) 0%, rgba(17, 24, 39, 0.95) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 100%)',
      backdropFilter: 'blur(10px)',
      border: `2px solid ${borderColor}`,
      borderRadius: '16px',
      padding: isExpanded ? '24px' : '16px',
      minHeight: isExpanded ? '320px' : '200px',
      width: isExpanded ? '100%' : '280px',
      margin: '0 8px',
      flexShrink: 0,
      position: 'relative',
      overflow: 'hidden',
      transform: isActive ? 'scale(1.02)' : 'scale(0.98)',
      opacity: isActive ? 1 : 0.8,
      transition: 'all 0.3s ease-out'
    }), [borderColor, isExpanded, isActive]);return (
      <motion.div
        className={`timer-card relative ${isExpanded ? 'expanded' : 'compact'}`}
        variants={cardVariants}
        initial="hidden"
        animate={isExpanded ? "expanded" : "visible"}
        transition={{ duration: 0.3, ease: "easeOut" }}
        style={cardStyles}
      >
        {/* Processing overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black bg-opacity-20 rounded-2xl flex items-center justify-center z-20">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 flex items-center space-x-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              <span className="text-sm font-medium">Processing...</span>
            </div>
          </div>
        )}        {/* Completion flash effect - optimized */}
        {isCompleting && (
          <div 
            className="absolute inset-0 rounded-2xl opacity-20 animate-pulse"
            style={{
              background: 'radial-gradient(circle at 50% 50%, #ef4444 0%, transparent 70%)',
              pointerEvents: 'none'
            }}
          />
        )}

        {/* Background glow effect - simplified */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-10 transition-opacity duration-1000"
          style={{
            background: `radial-gradient(circle at 50% 50%, ${borderColor} 0%, transparent 70%)`,
            pointerEvents: 'none'
          }}
        />
          {/* Header */}
        <div className="relative z-10 flex justify-between items-start mb-4">
          <div>
            <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {timer.goalName || 'Focus Session'}
            </h3>
            <div className="flex items-center space-x-2">
              <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {goal?.category || 'productivity'} • {timer.duration || 25} min
              </p>
              {isNearCompletion && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                  <Bell className="w-3 h-3 mr-1" />
                  Almost done!
                </span>
              )}
              {isCompleting && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 animate-pulse">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Finishing...
                </span>
              )}
            </div>
          </div>
          
          {/* Expand/Collapse button */}
          <button
            onClick={() => setExpandedTimer(isExpanded ? null : timer.id)}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
            }`}
          >
            {isExpanded ? (
              <Minimize2 className="w-5 h-5" />
            ) : (
              <Maximize2 className="w-5 h-5" />
            )}
          </button>
        </div>        {/* Timer Display - Optimized for smooth updates */}
        <div className="relative z-10 flex flex-col items-center mb-4">          
          {/* Circular Progress - Smooth transitions */}
          <div className="relative w-24 h-24 mb-3">
            <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke={isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
                strokeWidth="6"
                fill="none"
              />
              
              {/* Progress circle - Optimized for smooth updates without flicker */}
              <circle
                cx="50"
                cy="50"
                r="45"
                stroke={borderColor}
                strokeWidth="6"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={`${Math.round(progress * 2.83)} 283`}
                style={{
                  transition: 'stroke-dasharray 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.3s ease-out',
                  filter: `drop-shadow(0 0 6px ${borderColor}30)`,
                  willChange: 'auto' // Remove aggressive optimization that causes flicker
                }}
              />
            </svg>
              
            {/* Status indicator - Smooth state transitions */}
            <div 
              className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full transition-all duration-700 ease-out ${
                isCompleting ? 'bg-red-500' :
                isNearCompletion ? 'bg-orange-500' :
                isPaused ? 'bg-amber-500' : 'bg-green-500'
              } ${
                isCompleting ? 'animate-pulse' :
                isNearCompletion ? 'animate-pulse' :
                isPaused ? 'animate-pulse' : ''
              }`}
              style={{
                boxShadow: `0 0 8px ${borderColor}60`,
                transition: 'all 0.7s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            />
          </div>          

          {/* Time Remaining - Stable display with minimal flicker */}
          <div className="text-center">
            <div className={`text-2xl font-mono font-bold transition-colors duration-700 ease-out ${
              isCompleting ? 'text-red-500' :
              isNearCompletion ? 'text-orange-500' :
              isDarkMode ? 'text-white' : 'text-gray-900'
            } ${isCompleting ? 'animate-pulse' : ''}`}
              style={{
                minWidth: '80px', // Prevent layout shift during updates
                textAlign: 'center'
              }}
            >
              {formatTime(remainingTime)}
            </div>
            <div className={`text-sm transition-colors duration-500 ease-out ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {isCompleting ? 'Tamamlanıyor...' :
               isNearCompletion ? 'Neredeyse bitti!' :
               isPaused ? 'Duraklatıldı' : 'Çalışıyor'}
            </div>
            {progress > 0 && (
              <div className={`text-xs mt-1 transition-opacity duration-500 ease-out ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}
                style={{
                  minHeight: '16px' // Prevent layout shift
                }}
              >
                %{Math.round(progress)} tamamlandı
              </div>
            )}
          </div>
        </div>{/* Controls */}
        <div className="relative z-10 flex justify-center space-x-3">
          {/* Pause/Resume Button */}
          <motion.button
            onClick={() => handleTimerAction(isPaused ? 'resume' : 'pause', timer.id)}
            disabled={isProcessing}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isPaused
                ? 'bg-green-500 hover:bg-green-600 text-white'
                : 'bg-amber-500 hover:bg-amber-600 text-white'
            }`}
            whileHover={{ scale: isProcessing ? 1 : 1.05 }}
            whileTap={{ scale: isProcessing ? 1 : 0.95 }}
          >
            {isPaused ? (
              <>
                <PlayCircle className="w-4 h-4" />
                <span>Resume</span>
              </>
            ) : (
              <>
                <PauseCircle className="w-4 h-4" />
                <span>Pause</span>
              </>
            )}
          </motion.button>

          {/* Complete Button - Only show if near completion */}
          {progress > 80 && (
            <motion.button
              onClick={() => handleTimerAction('complete', timer.id)}
              disabled={isProcessing}
              className="flex items-center space-x-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isProcessing ? 1 : 1.05 }}
              whileTap={{ scale: isProcessing ? 1 : 0.95 }}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <CheckCircle className="w-4 h-4" />
              <span>Complete</span>
            </motion.button>
          )}

          {/* Stop Button */}
          <motion.button
            onClick={() => handleTimerAction('stop', timer.id)}
            disabled={isProcessing}
            className="flex items-center space-x-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: isProcessing ? 1 : 1.05 }}
            whileTap={{ scale: isProcessing ? 1 : 0.95 }}
          >
            <StopCircle className="w-4 h-4" />
            <span>Stop</span>
          </motion.button>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="relative z-10 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700"
          >
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Başlangıç:</span>
                <p className="font-medium">
                  {new Date(timer.startTime).toLocaleTimeString('tr-TR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
              <div>
                <span className="text-gray-500">Bitiş:</span>
                <p className="font-medium">
                  {new Date(timer.endTime).toLocaleTimeString('tr-TR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
              <div>
                <span className="text-gray-500">İlerleme:</span>
                <p className="font-medium">{Math.round(progress)}%</p>
              </div>
              <div>
                <span className="text-gray-500">XP Ödülü:</span>
                <p className="font-medium">{timer.xpReward || 0} XP</p>
              </div>
            </div>
            
            {goal?.description && (
              <div className="mt-3">
                <span className="text-gray-500 text-sm">Açıklama:</span>
                <p className="mt-1 text-sm">{goal.description}</p>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    );
  };

  // Empty state
  if (activeTimers.length === 0) {
    return (
      <div className={`${className} p-8 text-center`}>
        <div className={`rounded-xl p-8 border ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <Timer className={`w-16 h-16 mx-auto mb-4 ${
            isDarkMode ? 'text-gray-400' : 'text-gray-300'
          }`} />
          <h3 className={`text-xl font-semibold mb-2 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Aktif Timer Bulunmuyor
          </h3>
          <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Hedeflerinizden birini başlatarak odak seansına geçin
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={`${className} p-4`}>
      {/* Touch-friendly styles */}
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .touch-friendly-timer {
          touch-action: manipulation;
          -webkit-tap-highlight-color: transparent;
        }
        .touch-friendly-timer:active {
          transform: scale(0.98);
          transition: transform 0.1s ease;
        }
      `}</style>{/* Header with timer count */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Aktif Zamanlayıcılar
          </h3>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {activeTimers.length} / 5 aktif timer • Dokunmatik kontrolü
          </p>
        </div>
      </div>

      {/* Timer Container */}
      <div
        ref={containerRef}
        className="relative overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {expandedTimer ? (
          // Expanded single timer view
          <AnimatePresence mode="wait">
            {activeTimers
              .filter(timer => timer.id === expandedTimer)
              .map(timer => (
                <TimerCard
                  key={timer.id}
                  timer={timer}
                  index={0}
                  isActive={true}
                  isExpanded={true}
                />
              ))}
          </AnimatePresence>        ) : (
          // Touch-friendly slider view - No navigation buttons, just swipe and tap
          <div className="relative">
            <div className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 scrollbar-hide">
              <div 
                className="flex transition-transform duration-300 ease-in-out"
                style={{
                  transform: `translateX(-${currentIndex * 296}px)`, // 280px width + 16px margin
                  width: `${activeTimers.length * 296}px`
                }}
              >
                {activeTimers.map((timer, index) => (
                  <div 
                    key={timer.id} 
                    className="snap-center touch-friendly-timer"
                    onClick={(e) => handleTimerTap(index, e)}
                    style={{ 
                      cursor: 'pointer',
                      minWidth: '280px',
                      margin: '0 8px'
                    }}
                  >
                    <TimerCard
                      timer={timer}
                      index={index}
                      isActive={index === currentIndex}
                      isExpanded={false}
                    />
                  </div>
                ))}
              </div>
            </div>
            
            {/* Enhanced touch instruction with visual cues */}
            {activeTimers.length > 1 && (
              <div className="text-center mt-3 space-y-1">
                <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Timer'a dokunarak merkezleyin • Kaydırarak geçiş yapın
                </p>
                <div className="flex justify-center space-x-1">
                  {activeTimers.map((_, index) => (
                    <div
                      key={index}
                      className={`w-2 h-2 rounded-full transition-all duration-200 ${
                        index === currentIndex
                          ? 'bg-blue-500 scale-125'
                          : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>      {/* Touch-friendly Quick Actions */}
      {activeTimers.length > 0 && (
        <div className="mt-6 flex justify-center space-x-4">
          <button
            onClick={() => {
              const currentTimer = activeTimers[currentIndex] || activeTimers[0];
              if (currentTimer) {
                const isPaused = currentTimer.status === 'paused' || currentTimer.isPaused;
                handleTimerAction(isPaused ? 'resume' : 'pause', currentTimer.id);
              }
            }}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 touch-action-manipulation ${
              isDarkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
            } active:scale-95`}
          >
            <Timer className="w-5 h-5" />
            <span>Merkez Timer Kontrolü</span>
          </button>
        </div>
      )}    </div>
  );
});

export default ImprovedTimerSlider;
